/**
 * FarmRoadHistoryPanel (Version 2.0 - Refonte complète)
 *
 * Interface épurée pour consulter l'historique des analyses IA
 * - Cartes minimalistes en grid 3 colonnes
 * - Filtre avancé par indicateur critique
 * - Vue détaillée réutilisant l'interface d'analyse
 */

import React, { useState, useEffect } from 'react';
import {
  CircularProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination
} from '@mui/material';
import {
  History,
  Filter,
  Search
} from 'lucide-react';

import FarmRoadInsightsService from './FarmRoadInsightsService';
import FarmRoadInsightCardMini from './FarmRoadInsightCardMini';
import FarmRoadHistoricalViewModal from './FarmRoadHistoricalViewModal';

// Liste des indicateurs disponibles
const AVAILABLE_INDICATORS = [
  { id: 'dli', label: 'DLI (Daily Light Integral)' },
  { id: 'sunlight_duration', label: 'Durée d\'ensoleillement' },
  { id: 'radiation_mean_diurnal', label: 'Radiation moyenne diurne' },
  { id: 'temperature_mean', label: 'Température moyenne' },
  { id: 'temperature_diurnal', label: 'Température diurne' },
  { id: 'temperature_nocturnal', label: 'Température nocturne' },
  { id: 'temperature_amplitude', label: 'Amplitude thermique' },
  { id: 'temperature_heat_stress', label: 'Stress thermique (chaleur)' },
  { id: 'temperature_cold_stress', label: 'Stress thermique (froid)' },
  { id: 'vpd_diurnal', label: 'VPD diurne' },
  { id: 'vpd_mean', label: 'VPD moyen' },
  { id: 'soil_moisture', label: 'Humidité du substrat' },
  { id: 'co2_diurnal', label: 'CO2 diurne' },
  { id: 'co2_saturation', label: 'Saturation CO2' }
];

const FarmRoadHistoryPanel = ({ farmId, farmName, sectors = [] }) => {
  // États
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [insights, setInsights] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 30, offset: 0, hasMore: false });
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Filtres
  const [filters, setFilters] = useState({
    analysisPeriod: '',
    sectorId: '',           // Filtre par secteur
    criticalIndicator: '',  // Filtre par indicateur critique
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });

  // Charger l'historique
  useEffect(() => {
    if (farmId) {
      loadHistory();
    }
  }, [farmId, pagination.offset, filters]);

  /**
   * Charge l'historique
   */
  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      // Préparer les filtres pour l'API
      const apiFilters = {
        ...filters,
        limit: pagination.limit,
        offset: pagination.offset
      };

      // TODO: Ajouter le filtre par indicateur critique dans l'API
      // Pour l'instant, on filtre côté client

      const result = await FarmRoadInsightsService.getHistory(farmId, apiFilters);

      let filteredInsights = result.data || [];

      // Filtrage côté client par indicateur critique (temporaire)
      if (filters.criticalIndicator) {
        filteredInsights = filteredInsights.filter(insight => {
          const criticalList = insight.critical_indicators_list || [];
          const warningList = insight.warning_indicators_list || [];
          return criticalList.includes(filters.criticalIndicator) ||
                 warningList.includes(filters.criticalIndicator);
        });
      }

      setInsights(filteredInsights);
      setPagination(result.pagination || { total: 0, limit: 30, offset: 0, hasMore: false });

    } catch (err) {
      console.error('[HistoryPanel] Erreur:', err);
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gère le changement de page
   */
  const handlePageChange = (event, page) => {
    setPagination(prev => ({
      ...prev,
      offset: (page - 1) * prev.limit
    }));
  };

  /**
   * Gère le changement de filtres
   */
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  /**
   * Ouvre la vue détaillée
   */
  const handleConsult = (insight) => {
    setSelectedInsight(insight);
    setShowDetailModal(true);
  };

  /**
   * Supprime une analyse
   */
  const handleDeleteAnalysis = async (insightId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette analyse ?')) {
      return;
    }

    try {
      await FarmRoadInsightsService.deleteAnalysis(insightId);
      // Recharger l'historique après suppression
      loadHistory();
    } catch (err) {
      console.error('[HistoryPanel] Erreur suppression:', err);
      setError('Erreur lors de la suppression de l\'analyse');
    }
  };

  /**
   * Rendu de l'en-tête
   */
  const renderHeader = () => (
    <div className="mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Historique des Analyses</h2>
      <p className="text-sm text-gray-600">
        {pagination.total} analyse{pagination.total > 1 ? 's' : ''} • {farmName}
      </p>
    </div>
  );

  /**
   * Rendu des filtres
   */
  const renderFilters = () => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Filter className="h-5 w-5 text-gray-600" />
        <h3 className="font-semibold text-gray-800">Filtres de recherche</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Filtre par période */}
        <FormControl size="small" fullWidth>
          <InputLabel>Période d'analyse</InputLabel>
          <Select
            value={filters.analysisPeriod}
            onChange={(e) => handleFilterChange('analysisPeriod', e.target.value)}
            label="Période d'analyse"
          >
            <MenuItem value="">Toutes</MenuItem>
            <MenuItem value="24h">24 heures</MenuItem>
            <MenuItem value="daily">Journalier</MenuItem>
            <MenuItem value="weekly">Hebdomadaire</MenuItem>
            <MenuItem value="monthly">Mensuel</MenuItem>
          </Select>
        </FormControl>

        {/* Filtre par secteur */}
        <FormControl size="small" fullWidth>
          <InputLabel>Secteur</InputLabel>
          <Select
            value={filters.sectorId}
            onChange={(e) => handleFilterChange('sectorId', e.target.value)}
            label="Secteur"
          >
            <MenuItem value="">Tous les secteurs</MenuItem>
            {sectors.map(sector => (
              <MenuItem key={sector.id || sector.sectorId} value={sector.id || sector.sectorId}>
                {sector.name || sector.id || sector.sectorId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Filtre par indicateur critique */}
        <FormControl size="small" fullWidth>
          <InputLabel>Indicateur en alerte</InputLabel>
          <Select
            value={filters.criticalIndicator}
            onChange={(e) => handleFilterChange('criticalIndicator', e.target.value)}
            label="Indicateur en alerte"
          >
            <MenuItem value="">Tous</MenuItem>
            {AVAILABLE_INDICATORS.map(ind => (
              <MenuItem key={ind.id} value={ind.id}>{ind.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {renderHeader()}
      {renderFilters()}

      {/* Message d'erreur */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Chargement */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <CircularProgress />
        </div>
      )}

      {/* Aucun résultat */}
      {!loading && insights.length === 0 && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-8 text-center">
          <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">Aucune analyse trouvée</p>
          <p className="text-sm text-gray-500">
            {filters.criticalIndicator || filters.analysisPeriod
              ? 'Essayez de modifier vos filtres'
              : 'Les analyses IA effectuées apparaîtront ici'}
          </p>
        </div>
      )}

      {/* Grid 3 colonnes de cartes minimalistes */}
      {!loading && insights.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {insights.map(insight => (
            <FarmRoadInsightCardMini
              key={insight.insight_id}
              insight={insight}
              onConsult={() => handleConsult(insight)}
              onDelete={() => handleDeleteAnalysis(insight.insight_id)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && pagination.total > pagination.limit && (
        <div className="flex justify-center mt-6">
          <Pagination
            count={Math.ceil(pagination.total / pagination.limit)}
            page={Math.floor(pagination.offset / pagination.limit) + 1}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </div>
      )}

      {/* Modal de vue détaillée */}
      {selectedInsight && (
        <FarmRoadHistoricalViewModal
          open={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          insight={selectedInsight}
        />
      )}
    </div>
  );
};

export default FarmRoadHistoryPanel;
